import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  timeout: 10000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // You can add authentication tokens here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.status, error.response.data);
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network Error:', error.request);
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Account API functions
export const accountApi = {
  // Get all accounts
  getAllAccounts: () => {
    console.log('API call: GET /accounts');
    return api.get('/accounts');
  },
  
  // Get account by ID
  getAccount: (id) => api.get(`/accounts/${id}`),
  
  // Create new account
  createAccount: (account) => api.post('/accounts', account),
  
  // Update account
  updateAccount: (id, account) => api.put(`/accounts/${id}`, account),
  
  // Delete account
  deleteAccount: (id) => api.delete(`/accounts/${id}`),
  
  // Toggle account status
  toggleAccountStatus: (id) => api.post(`/accounts/${id}/toggle-status`),

  // Persist a new manual display order for a set of accounts
  reorderAccounts: (accountIds) => api.post('/accounts/reorder', { account_ids: accountIds }),
  
  // Get account transactions
  getTransactions: (id, ascending = false) => 
    api.get(`/accounts/${id}/transactions`, { params: { ascending } }),
  
  // Add transaction to account
  addTransaction: (id, transaction) => 
    api.post(`/accounts/${id}/transactions`, transaction),

  // Update transaction
  updateTransaction: (accountId, transactionId, transaction) =>
    api.put(`/accounts/${accountId}/transactions/${transactionId}`, transaction),
  
  // Delete transaction
  deleteTransaction: (accountId, transactionId) =>
    api.delete(`/accounts/${accountId}/transactions/${transactionId}`),
  
  // Get account valuations
  getValuations: (id) => api.get(`/accounts/${id}/valuations`),
  
  // Add valuation to account
  addValuation: (id, valuation) => 
    api.post(`/accounts/${id}/valuations`, valuation),
  
  // Delete valuation from account
  deleteValuation: (id, valuationId) => 
    api.delete(`/accounts/${id}/valuations/${valuationId}`),
};

// Stats API functions
// 용돈 모으기
export const allowanceApi = {
  getItems: () => api.get('/allowance/items'),
  createItem: (data) => api.post('/allowance/items', data),
  updateItem: (id, data) => api.put(`/allowance/items/${id}`, data),
  deleteItem: (id) => api.delete(`/allowance/items/${id}`),

  getEarnings: (params) => api.get('/allowance/earnings', { params }),
  createEarning: (data) => api.post('/allowance/earnings', data),
  deleteEarning: (id) => api.delete(`/allowance/earnings/${id}`),

  getSummary: () => api.get('/allowance/summary'),
};

export const statsApi = {
  // Get total assets
  getTotalAssets: () => api.get('/stats/total-assets'),
  
  // Get total cash
  getTotalCash: () => api.get('/stats/total-cash'),
  
  // Get monthly income breakdown
  getMonthlyIncomeBreakdown: (year) => 
    api.get('/stats/monthly-income-breakdown', { params: { year } }),
  
  // Get salaries
  getSalaries: (year) => api.get('/stats/salaries', { params: { year } }),

  // Get median salary
  getSalaryMedian: (year, person) => api.get('/stats/salaries/median', { params: { year, person } }),

  // Get monthly salary totals
  getMonthlySalaryTotals: (year, person) => api.get('/stats/salaries/monthly-totals', { params: { year, person } }),
  
  // Add salary
  addSalary: (amount, month, person, classification, memo) =>
    api.post('/stats/salaries', null, { params: { amount, month, person, classification, memo } }),

  // Update salary
  updateSalary: (index, amount, month, person, classification, memo) =>
    api.put(`/stats/salaries/${index}`, null, { params: { amount, month, person, classification, memo } }),
  
  // Delete salary
  deleteSalary: (index) => 
    api.delete(`/stats/salaries/${index}`),
  
  // Get asset allocation
  getAssetAllocation: () => api.get('/stats/asset-allocation'),

  // Saving points & growth vs snapshot
  getAssetSnapshots: () => api.get('/stats/asset-snapshots'),
  createAssetSnapshot: (label) =>
    api.post('/stats/asset-snapshots', { label: label || null }),
  deleteAssetSnapshot: (snapshotId) => api.delete(`/stats/asset-snapshots/${snapshotId}`),
  getAssetGrowth: (baselineId) =>
    api.get('/stats/asset-growth', {
      params: baselineId ? { baseline_id: baselineId } : {},
    }),

  // Stock portfolio (weights)
  getStockPortfolio: () => api.get('/stats/stock-portfolio'),
  getStockPortfolioNotes: () => api.get('/stats/stock-portfolio-notes'),
  updateStockPortfolioNote: (person, note) =>
    api.put(`/stats/stock-portfolio-notes/${person}`, { note }),
  addStockEntry: (person, category, market, listingMarket, name, purchaseAmount, currentAmount) =>
    api.post('/stats/stock-portfolio', {
      person,
      category,
      market,
      listing_market: listingMarket,
      name,
      amount: currentAmount,
      purchase_amount: purchaseAmount,
      current_amount: currentAmount,
    }),
  reorderStockEntries: (person, entryIds) =>
    api.post(`/stats/stock-portfolio/${person}/reorder`, { entry_ids: entryIds }),
  updateStockEntry: (person, entryId, category, market, listingMarket, name, purchaseAmount, currentAmount) =>
    api.put(`/stats/stock-portfolio/${person}/${entryId}`, {
      person,
      category,
      market,
      listing_market: listingMarket,
      name,
      amount: currentAmount,
      purchase_amount: purchaseAmount,
      current_amount: currentAmount,
    }),
  deleteStockEntry: (person, entryId) => api.delete(`/stats/stock-portfolio/${person}/${entryId}`),
  previewStockOcrAiImages: (person, files) => {
    const formData = new FormData();
    formData.append('person', person);
    Array.from(files || []).forEach((file) => formData.append('files', file));
    return api.post('/stats/stock-portfolio/ocr-preview-ai-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  applyStockOcrUpdates: (person, updates) =>
    api.post('/stats/stock-portfolio/ocr-apply', { person, updates }),

  // 여행 비용 — 여행 이름은 한글·공백이 섞이므로 주소 경로가 아니라 쿼리/본문으로 보낸다.
  getTrips: () => api.get('/stats/trips'),
  getTrip: (key) => api.get('/stats/trips/detail', { params: { key } }),
  updateTrip: (key, { title, start_date, end_date }) =>
    api.put('/stats/trips', { key, title, start_date, end_date }),
  mergeTrip: (source, target) => api.post('/stats/trips/merge', { source, target }),
  unmergeTrip: (source) => api.post('/stats/trips/unmerge', { source }),

  // Year-end tax
  getYearEndTax: (year) => api.get('/stats/year-end-tax', { params: { year } }),
  addYearEndTaxEntry: (entry) => api.post('/stats/year-end-tax', entry),
  updateYearEndTaxGrossIncome: (year, person, grossIncome) =>
    api.put('/stats/year-end-tax/gross-income', { year, person, gross_income: grossIncome }),
  updateYearEndTaxEntry: (entryId, entry) => api.put(`/stats/year-end-tax/${entryId}`, entry),
  deleteYearEndTaxEntry: (entryId) => api.delete(`/stats/year-end-tax/${entryId}`),
  importCashReceipts: (year, person, files) => {
    const formData = new FormData();
    formData.append('year', year);
    formData.append('person', person);
    Array.from(files || []).forEach((file) => formData.append('files', file));
    return api.post('/stats/year-end-tax/import-cash-receipts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
};

// Memo API functions
export const memoApi = {
  getAll: () => api.get('/memos'),
  create: (content = '') => api.post('/memos', { content }),
  update: (id, content) => api.put(`/memos/${id}`, { content }),
  delete: (id) => api.delete(`/memos/${id}`),
};

export const bucketApi = {
  getAll: () => api.get('/bucket'),
  create: (title, category) => api.post('/bucket', { title, category }),
  setDone: (id, done) => api.patch(`/bucket/${id}`, { done }),
  setCategory: (id, category) => api.patch(`/bucket/${id}`, { category }),
  delete: (id) => api.delete(`/bucket/${id}`),
};

// Utility function to format currency
export const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '₩0';
  return `₩${amount.toLocaleString()}`;
};

// Utility function to get text color based on background
export const getTextColor = (color) => {
  if (!color) return '#333';
  // Convert to lowercase for comparison
  const normalizedColor = color.toLowerCase();
  // Only return white text if background is pure black
  if (normalizedColor === '#000000' || normalizedColor === '#000') {
    return '#ffffff';
  }
  // For all other colors, return black text
  return '#000000';
};
